import { useState } from 'react';
import {
  Check,
  Copy,
  ThumbsDown,
  ThumbsUp,
  WarningCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { sendAssistantFeedback } from '@/api/hooks/useAiAssistant';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { MarkdownMessage } from '@/components/assistant/markdown-message';
import { ToolTrail } from '@/components/assistant/tool-trail';
import type { AssistantMessage } from '@/pages/assistant/conversations';

function CopyAnswerButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="Copy answer as markdown"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(content);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error('Clipboard unavailable');
        }
      }}
      className="rounded-md p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground group-hover/msg:opacity-100"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-success" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

/**
 * Thumbs up / down on a finished answer. Down opens a one-line "what was
 * wrong?" box; both send the question and answer so the record stands alone.
 */
function FeedbackButtons({
  message,
  question,
  conversationId,
  onRecorded,
}: {
  message: AssistantMessage;
  question: string;
  conversationId?: string;
  onRecorded?: (messageId: string, rating: 'UP' | 'DOWN') => void;
}) {
  const [askingNote, setAskingNote] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const given = message.feedback;

  const send = async (rating: 'UP' | 'DOWN', withNote?: string) => {
    setBusy(true);
    try {
      await sendAssistantFeedback({
        rating,
        question,
        answer: message.content,
        note: withNote?.trim() || undefined,
        conversationId,
        messageId: message.id,
        model: message.model,
      });
      onRecorded?.(message.id, rating);
      setAskingNote(false);
      setNote('');
      toast.success(
        rating === 'UP' ? 'Thanks — noted.' : 'Thanks — logged for review.',
      );
    } catch {
      toast.error('Could not send feedback');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1.5">
      <div className="flex items-center gap-0.5 opacity-70 transition-opacity group-hover/msg:opacity-100">
        <button
          type="button"
          title="Good answer"
          aria-label="Good answer"
          aria-pressed={given === 'UP'}
          disabled={busy}
          onClick={() => void send('UP')}
          data-testid="assistant-feedback-up"
          className={cn(
            'rounded-md p-1 text-muted-foreground/70 hover:bg-muted hover:text-foreground',
            given === 'UP' && 'text-success',
          )}
        >
          <ThumbsUp
            className="h-3.5 w-3.5"
            weight={given === 'UP' ? 'fill' : 'regular'}
          />
        </button>
        <button
          type="button"
          title="Wrong or unclear"
          aria-label="Wrong or unclear"
          aria-pressed={given === 'DOWN'}
          disabled={busy}
          onClick={() => setAskingNote((v) => !v)}
          data-testid="assistant-feedback-down"
          className={cn(
            'rounded-md p-1 text-muted-foreground/70 hover:bg-muted hover:text-foreground',
            given === 'DOWN' && 'text-error',
          )}
        >
          <ThumbsDown
            className="h-3.5 w-3.5"
            weight={given === 'DOWN' ? 'fill' : 'regular'}
          />
        </button>
      </div>
      {askingNote && (
        <div className="mt-1.5 flex items-start gap-1.5">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="What was wrong or unclear? (optional)"
            data-testid="assistant-feedback-note"
            className="min-h-0 flex-1 text-xs"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            disabled={busy}
            onClick={() => void send('DOWN', note)}
            data-testid="assistant-feedback-send"
          >
            Send
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * One thread's messages — user bubbles and assistant answers with their
 * activity trail — rendered identically on the full page and in the widget.
 */
export function AssistantMessageList({
  messages,
  streamingMessageId,
  compact = false,
  conversationId,
  onFeedback,
  onRetry,
}: {
  messages: AssistantMessage[];
  streamingMessageId: string | null;
  /** Tighter spacing for the floating widget. */
  compact?: boolean;
  conversationId?: string;
  onFeedback?: (messageId: string, rating: 'UP' | 'DOWN') => void;
  /** Re-ask the question a failed answer was for. */
  onRetry?: (messageId: string) => void;
}) {
  const questionFor = (index: number): string => {
    for (let i = index - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].content;
    }
    return '';
  };
  return (
    <div className={cn('space-y-5', compact && 'space-y-4')}>
      {messages.map((m, index) =>
        m.role === 'user' ? (
          <div key={m.id} className="flex justify-end">
            <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary/10 px-3.5 py-2 text-sm">
              {m.content}
            </div>
          </div>
        ) : (
          <div key={m.id} className="group/msg">
            <ToolTrail
              toolEvents={m.toolEvents ?? []}
              thinking={m.thinking}
              streaming={m.id === streamingMessageId}
            />
            {m.content ? (
              <div className="flex items-start gap-1">
                <MarkdownMessage content={m.content} className="flex-1" />
                {m.id !== streamingMessageId && (
                  <CopyAnswerButton content={m.content} />
                )}
              </div>
            ) : m.id === streamingMessageId ? (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-primary/70" />
                composing…
              </div>
            ) : null}
            {m.id === streamingMessageId && m.content && (
              <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-primary/70 align-text-bottom" />
            )}
            {m.error && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-error/30 bg-error-bg px-3 py-2 text-xs text-error-text">
                <WarningCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="flex-1">{m.error}</span>
                {onRetry &&
                  // Only the latest answer, and only when nothing is
                  // streaming: retrying an older one would rewrite the
                  // thread after it.
                  index === messages.length - 1 &&
                  streamingMessageId === null && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 shrink-0 px-2 text-xs"
                      onClick={() => onRetry(m.id)}
                      data-testid="assistant-retry"
                    >
                      Retry
                    </Button>
                  )}
              </div>
            )}
            {m.stats && (
              <div className="mt-1.5 text-[10px] tabular-nums text-muted-foreground/60">
                {(m.stats.durationMs / 1000).toFixed(1)}s · {m.stats.rounds}{' '}
                round{m.stats.rounds === 1 ? '' : 's'} ·{' '}
                {(m.stats.outputTokens / 1000).toFixed(1)}k tokens
              </div>
            )}
            {m.content && m.id !== streamingMessageId && !m.error && (
              <FeedbackButtons
                message={m}
                question={questionFor(index)}
                conversationId={conversationId}
                onRecorded={onFeedback}
              />
            )}
          </div>
        ),
      )}
    </div>
  );
}
